/**
 * Server clock offset estimation (NTP-style) from `ping`/`pong`.
 *
 * Each sample gives rtt = t1 − t0 and offset = serverTime − (t0 + t1) / 2 (assuming symmetric paths). The error of
 * one sample is at most rtt / 2, so the estimate uses the sample with the LOWEST rtt among the recent window
 * (queueing delay only ever adds time), and the reported RTT is a smoothed mean for display.
 *
 * Times: `t0`/`t1` are the client's monotonic clock (performance.now()); `serverTime` is the server's Date.now().
 * `offsetMs` converts server time → local monotonic time: local = serverTime − offsetMs.
 */
export interface ClockSample {
  rttMs: number;
  offsetMs: number;
}

export class ClockSync {
  private readonly samples: ClockSample[] = [];
  private smoothedRtt = 0;
  /** serverTime − localTime (ms). 0 until the first sample. */
  offsetMs = 0;
  /** Best-sample RTT (ms) behind `offsetMs`. */
  bestRttMs = Number.POSITIVE_INFINITY;

  constructor(private readonly windowSize = 8) {}

  get sampleCount(): number {
    return this.samples.length;
  }

  /** Smoothed RTT for display (ms), 0 before any sample. */
  get rttMs(): number {
    return this.smoothedRtt;
  }

  /** Feed one ping/pong: local send time, local receive time, server time in the pong. Returns false if rejected. */
  add(t0: number, t1: number, serverTime: number): boolean {
    const rtt = t1 - t0;
    if (!Number.isFinite(rtt) || rtt < 0 || !Number.isFinite(serverTime)) return false;
    const sample: ClockSample = { rttMs: rtt, offsetMs: serverTime - (t0 + t1) / 2 };
    this.samples.push(sample);
    if (this.samples.length > this.windowSize) this.samples.shift();
    this.smoothedRtt = this.smoothedRtt === 0 ? rtt : this.smoothedRtt * 0.8 + rtt * 0.2;
    let best = this.samples[0]!;
    for (const s of this.samples) if (s.rttMs < best.rttMs) best = s;
    this.offsetMs = best.offsetMs;
    this.bestRttMs = best.rttMs;
    return true;
  }

  /** Server timestamp (ms) → local monotonic time (ms). */
  toLocal(serverTime: number): number {
    return serverTime - this.offsetMs;
  }

  /** Local monotonic time (ms) → server timestamp (ms). */
  toServer(localTime: number): number {
    return localTime + this.offsetMs;
  }

  /** Welcome-time coarse estimate (before any ping): assume the welcome arrived half an RTT after it was sent. */
  seed(serverTime: number, receivedAt: number): void {
    if (this.samples.length === 0) this.offsetMs = serverTime - receivedAt;
  }

  reset(): void {
    this.samples.length = 0;
    this.smoothedRtt = 0;
    this.offsetMs = 0;
    this.bestRttMs = Number.POSITIVE_INFINITY;
  }
}

/**
 * Exponential reconnect backoff with jitter: base·2^attempt capped at max, ±jitter fraction. `random` is
 * injectable (tests).
 */
export const backoffDelay = (
  attempt: number,
  random: () => number = Math.random,
  baseMs = 500,
  maxMs = 10_000,
  jitter = 0.25,
): number => {
  const raw = Math.min(maxMs, baseMs * 2 ** Math.max(0, attempt));
  const j = 1 + (random() * 2 - 1) * jitter;
  return Math.max(0, Math.round(raw * j));
};

/**
 * Token bucket that keeps us under the server's text-message rate limit (RATE_LIMIT_TEXT) with headroom: excess
 * messages wait in a FIFO instead of being rejected server-side (ICE candidate bursts).
 */
export class TokenBucket {
  private tokens: number;
  private last: number;

  constructor(
    private readonly burst: number,
    private readonly perSecond: number,
    now: number,
  ) {
    this.tokens = burst;
    this.last = now;
  }

  private refill(now: number): void {
    const dt = Math.max(0, now - this.last) / 1000;
    this.last = now;
    this.tokens = Math.min(this.burst, this.tokens + dt * this.perSecond);
  }

  /** Take one token if available. */
  take(now: number): boolean {
    this.refill(now);
    if (this.tokens >= 1) {
      this.tokens -= 1;
      return true;
    }
    return false;
  }

  /** ms until one token is available. */
  waitMs(now: number): number {
    this.refill(now);
    return this.tokens >= 1 ? 0 : Math.ceil(((1 - this.tokens) / this.perSecond) * 1000);
  }
}
